/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  IconButton,
  Button,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CSVProp } from 'componentregistry';
import CodeInput from 'custom/CodeInput';
import { helpTextSelector } from 'designer/state';
import Icon from 'icons/Icon';
import Papa from 'papaparse';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo, useState } from 'react';
import useComponentSize from 'hooks/useComponentSize';
import { useTheme } from 'theme/Theme';
import { isEmpty } from 'utils/isEmpty';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  tip: {
    cursor: 'default',
  },
});

function CSVDialog({
  value,
  onSubmit,
  onClose,
}: {
  value: string;
  onSubmit: (val: string) => void;
  onClose: () => void;
}) {
  const [csvData, setCsvData] = useState(value);
  const platform = usePlatform();
  const theme = useTheme();
  const s = useComponentSize();

  return (
    <Dialog fullScreen open onClose={onClose}>
      <DialogTitle>
        CSV Data
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 16 }}
          aria-label="close"
          size="medium"
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon width={24} height={24} iconName="times" />
        </IconButton>
      </DialogTitle>
      <Typography sx={{ marginLeft: '24px' }} variant="caption">
        Copy &amp; Paste CSV data (Max 20K lines). The first row must be the
        field headers. The second row will determine the data type of the fields
        and hence must not have any empty values.
        <ul>
          <li>
            Date values must be in ISO format e.g. 2021-12-31 or
            2021-12-31T23:59:59.123Z.
          </li>
          <li>
            A value &quot;true&quot; or &quot;false&quot; will be treated as
            boolean.
          </li>
          <li>
            &quot;Y&quot; or &quot;N&quot; will be treated as YN type that are
            used to render Checkboxes.
          </li>
          <li>
            &quot;T&quot; or &quot;F&quot; will be treated as TF type that are
            used to render Checkboxes.
          </li>
        </ul>
      </Typography>
      <DialogContent ref={s.ref} sx={{ overflow: 'hidden', p: 0 }}>
        <CodeInput
          darkMode={theme.palette.mode !== 'light'}
          height={s.height}
          width={s.width}
          value={csvData ?? ''}
          onChange={setCsvData}
          language="plaintext"
        />
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<Icon parentColor="primary" iconName="check" />}
          onClick={async () => {
            const csv = Papa.parse<string[]>(csvData.trim(), {
              delimiter: ',',
              header: false,
            });
            if (csv.errors.length) {
              platform.showError(
                csv.errors
                  .map(({ message, row }) => `${message} at row ${row}`)
                  .join('\n'),
              );
              return;
            }
            if (csv.data.length < 2) {
              platform.showError(
                'You must provide atleast one line of data apart from the header line.',
              );
              return;
            }
            if (csv.data.length > 20000) {
              platform.showError(
                'You can load only a max of 20K rows at a time.',
              );
              return;
            }
            const headers = csv.data[0];
            const len = headers.length;
            const index = csv.data.findIndex((r) => r.length !== len);
            if (index !== -1) {
              platform.showError(
                `Invalid CSV data. Found ${
                  csv.data[index].length
                } values at line ${
                  index + 1
                } where as all the previous lines contain ${
                  csv.data[index - 1].length
                } values!`,
              );
              return;
            }
            const data = csv.data[1];
            const ind = data.findIndex((val) => isEmpty(val));
            if (ind !== -1) {
              platform.showError(
                `Found an empty value in row 1, column ${
                  ind + 1
                }. The line following the headers must not have empty values! It is used to determine the data type of all the fields.`,
              );
              return;
            }
            onSubmit(csvData);
          }}
          variant="contained"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CSVProperty({
  appUid,
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<CSVProp, string>) {
  const classes = useStyles();
  const [edit, setEdit] = useState(false);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  const val =
    value && value.length > 2000
      ? `CSV too large! - ${value.length} characters
Click the edit icon to view/edit.`
      : value ?? '';

  return (
    <>
      <TextField
        disabled={isViewOnlyUser}
        data-testid={property}
        className={classes.root}
        fullWidth
        label={def.label}
        variant="standard"
        required={required}
        spellCheck={false}
        value={val}
        multiline
        minRows={5}
        maxRows={15}
        onFocus={(e) => {
          e.target.select();
        }}
        classes={{ root: classes.input }}
        InputProps={{
          startAdornment: (
            <InputAdornment
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                setEdit(true);
              }}
              position="start"
            >
              <Icon iconName="edit" data-tip="Edit" />
            </InputAdornment>
          ),
          endAdornment: helpText ? (
            <InputAdornment position="end">
              <Icon
                className={classes.tip}
                iconName="comment-exclamation"
                data-tip={helpText}
                data-tip-delay={0}
                data-tip-at="top"
              />
            </InputAdornment>
          ) : undefined,
        }}
      />
      {edit ? (
        <CSVDialog
          value={value}
          onSubmit={(_val) => {
            onChange(property, _val, true);
            setEdit(false);
          }}
          onClose={() => {
            setEdit(false);
          }}
        />
      ) : null}
    </>
  );
}

export default memo(CSVProperty);
